import { BlockType } from "../../../types";
import type { FormActivityUI, FormAlternativeUI, FormBlockUI } from "./utils";

/**
 * SCRUM-795: transformaciones puras del estado de las alternativas sin
 * manipulativos.
 *
 * Viven acá y no dentro del contexto del formulario para que la parte con
 * lógica — inicializar la lista, renumerar bloques, limpiar el recurso al
 * cambiar de tipo — se pueda probar sin montar el formulario entero. El
 * contexto queda con envoltorios de tres líneas sobre `setLesson`.
 */

/** Una alternativa recién agregada, con un bloque de texto listo para escribir. */
export const createEmptyAlternative = (seedId: number): FormAlternativeUI => ({
  id: seedId,
  title: "",
  duration: 0,
  is_homework: false,
  is_socioemotional: false,
  socioemotional_main_goal: "",
  socioemotional_support_goal: "",
  summary: "",
  mainDevelopmentDomains: [],
  interactionTypes: [],
  typesOfEducationalActivities: [],
  bloom_level: [],
  primaria_interaction_type: [],
  evidence_type: [],
  activity_nature: [],
  blocks: [createEmptyAlternativeBlock(seedId + 1, 0)],
});

/** Un bloque vacío de una alternativa. */
export const createEmptyAlternativeBlock = (
  seedId: number,
  orderBy: number,
): FormBlockUI => ({
  id: seedId,
  type: BlockType.TEXT,
  order_by: orderBy,
  text: "",
  file_id: null,
  file_id_url: null,
  materials: "",
  resourceId: null,
  resourceFileId: null,
});

const mapActivity = (
  activities: FormActivityUI[],
  activityIndex: number,
  transform: (activity: FormActivityUI) => FormActivityUI,
): FormActivityUI[] =>
  activities.map((activity, index) =>
    index === activityIndex ? transform(activity) : activity,
  );

const mapAlternative = (
  activities: FormActivityUI[],
  activityIndex: number,
  alternativeIndex: number,
  transform: (alternative: FormAlternativeUI) => FormAlternativeUI,
): FormActivityUI[] =>
  mapActivity(activities, activityIndex, (activity) => ({
    ...activity,
    alternatives: (activity.alternatives ?? []).map((alternative, index) =>
      index === alternativeIndex ? transform(alternative) : alternative,
    ),
  }));

export const addAlternative = (
  activities: FormActivityUI[],
  activityIndex: number,
  seedId: number,
): FormActivityUI[] =>
  mapActivity(activities, activityIndex, (activity) => ({
    ...activity,
    // `?? []` porque una actividad manipulativa guardada antes de SCRUM-795
    // llega sin el campo.
    alternatives: [
      ...(activity.alternatives ?? []),
      createEmptyAlternative(seedId),
    ],
  }));

export const removeAlternative = (
  activities: FormActivityUI[],
  activityIndex: number,
  alternativeIndex: number,
): FormActivityUI[] =>
  mapActivity(activities, activityIndex, (activity) => ({
    ...activity,
    // Queda un array vacío, nunca undefined: el guardado lee esa diferencia
    // como "borrá las alternativas" en lugar de "no las toqué".
    alternatives: (activity.alternatives ?? []).filter(
      (_, index) => index !== alternativeIndex,
    ),
  }));

export const updateAlternative = <K extends keyof FormAlternativeUI>(
  activities: FormActivityUI[],
  activityIndex: number,
  alternativeIndex: number,
  key: K,
  value: FormAlternativeUI[K],
): FormActivityUI[] =>
  mapAlternative(
    activities,
    activityIndex,
    alternativeIndex,
    (alternative) => ({ ...alternative, [key]: value }),
  );

export const addAlternativeBlock = (
  activities: FormActivityUI[],
  activityIndex: number,
  alternativeIndex: number,
  seedId: number,
): FormActivityUI[] =>
  mapAlternative(
    activities,
    activityIndex,
    alternativeIndex,
    (alternative) => ({
      ...alternative,
      blocks: [
        ...alternative.blocks,
        createEmptyAlternativeBlock(seedId, alternative.blocks.length),
      ],
    }),
  );

export const removeAlternativeBlock = (
  activities: FormActivityUI[],
  activityIndex: number,
  alternativeIndex: number,
  blockIndex: number,
): FormActivityUI[] =>
  mapAlternative(
    activities,
    activityIndex,
    alternativeIndex,
    (alternative) => ({
      ...alternative,
      blocks: alternative.blocks.filter((_, index) => index !== blockIndex),
    }),
  );

export const updateAlternativeBlock = <K extends keyof FormBlockUI>(
  activities: FormActivityUI[],
  activityIndex: number,
  alternativeIndex: number,
  blockIndex: number,
  key: K,
  value: FormBlockUI[K],
): FormActivityUI[] =>
  mapAlternative(
    activities,
    activityIndex,
    alternativeIndex,
    (alternative) => ({
      ...alternative,
      blocks: alternative.blocks.map((block, index) =>
        index !== blockIndex
          ? block
          : {
              ...block,
              [key]: value,
              // Cambiar el tipo de bloque suelta el recurso: un TEXT no puede
              // quedarse con el que tenía cuando era TEXT_WITH_RESOURCE.
              ...(key === "type"
                ? { resourceId: null, resourceFileId: null }
                : {}),
            },
      ),
    }),
  );
